import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DummyCheckoutFields from '../components/DummyCheckoutFields';
import FallbackImage from '../components/FallbackImage';
import { useCart } from '../components/CartDrawer';
import { sampleProductsPath } from '../lib/paths';
import { getProducts } from '../lib/products';

export default function CartPage() {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity } = useCart();
  const productImages = useMemo(
    () =>
      Object.fromEntries(
        getProducts().flatMap((product) => [
          [product.slug, product.imageUrl],
          [product.variant_id, product.imageUrl],
        ])
      ),
    []
  );
  const subtotal = items.reduce((sum, item) => sum + item.mrp * item.quantity, 0);
  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = subtotal - total;
  const disableCheckoutButton = items.some((item) => !item.variant_id);

  useEffect(() => {
    document.title = 'Cart | Nyafaz Skincare';
  }, []);

  function checkout() {
    if (disableCheckoutButton) return;
    navigate(sampleProductsPath('/checkout'));
  }

  return (
    <main className="page">
      <section className="hero">
        <h1>Your Cart</h1>
        <p>Adjust quantities, review savings and continue to checkout.</p>
      </section>

      {!items.length ? (
        <div className="empty">
          Your cart is empty.
          <div style={{ marginTop: 16 }}>
            <Link to={sampleProductsPath('/products')} className="btn">
              Shop Products
            </Link>
          </div>
        </div>
      ) : (
        <div className="cart-layout">
          <section className="section-card" style={{ marginTop: 0 }}>
            {items.map((item) => (
              <article className="cart-row" key={item.slug}>
                <FallbackImage
                  src={item.imageUrl || productImages[item.slug] || productImages[item.variant_id]}
                  alt={item.name}
                />
                <div>
                  <h2 className="product-title">{item.name}</h2>
                  <p className="muted">
                    SKU: {item.sku} • Variant: {item.variant_id || 'Unavailable'}
                  </p>
                  <p>
                    ₹{item.price} <span className="mrp">₹{item.mrp}</span>
                  </p>
                  <div className="actions">
                    <span className="qty">
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={() => updateQuantity(item.slug, item.quantity - 1)}
                      >
                        -
                      </button>
                      {item.quantity}
                      <button
                        className="btn ghost"
                        type="button"
                        onClick={() => updateQuantity(item.slug, item.quantity + 1)}
                      >
                        +
                      </button>
                    </span>
                    <button className="btn secondary" type="button" onClick={() => removeItem(item.slug)}>
                      Remove
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </section>

          <aside className="section-card cart-summary" style={{ marginTop: 0 }}>
            <h2>Order Summary</h2>
            <p className="price-row">
              <span>MRP</span>
              <strong>₹{subtotal}</strong>
            </p>
            <p className="price-row">
              <span>Discount</span>
              <strong>-₹{discount}</strong>
            </p>
            <p className="price-row">
              <span>Total</span>
              <strong className="price">₹{total}</strong>
            </p>
            <DummyCheckoutFields />
            <button
              className="btn"
              type="button"
              style={{ width: '100%', marginTop: 16 }}
              onClick={checkout}
              disabled={disableCheckoutButton}
            >
              {disableCheckoutButton ? 'Checkout unavailable' : 'Checkout'}
            </button>
          </aside>
        </div>
      )}
    </main>
  );
}
